/**
 * SupportChatContext — Landlord
 * Provides support chat state and widget visibility across navigation.
 *
 * LAZY INITIALIZATION: The provider does NOT call useSupport() until
 * `activate()` is called (typically when the user visits /support).
 * This avoids eagerly connecting to Centrifugo on every page load.
 * Once activated, the hook stays alive and the widget works everywhere.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useSupport, type UseSupportReturn } from '../hooks/useSupport';

interface SupportChatContextValue extends UseSupportReturn {
  isWidgetOpen: boolean;
  setWidgetOpen: (open: boolean) => void;
  hasActiveChat: boolean;
  /** Call once to start loading support data and Centrifugo subscription */
  activate: () => void;
}

const SupportChatContext = createContext<SupportChatContextValue | undefined>(undefined);

interface SupportChatProviderProps {
  children: ReactNode;
}

/** Inner component that mounts useSupport — only rendered when activated */
function ActiveSupportProvider({
  children,
  isWidgetOpen,
  setWidgetOpen,
}: {
  children: ReactNode;
  isWidgetOpen: boolean;
  setWidgetOpen: (open: boolean) => void;
}) {
  const support = useSupport();
  const hasActiveChat = support.messages.length > 0;
  const activate = useCallback(() => {}, []); // no-op when already active

  const value: SupportChatContextValue = {
    ...support,
    isWidgetOpen,
    setWidgetOpen,
    hasActiveChat,
    activate,
  };

  return (
    <SupportChatContext.Provider value={value}>
      {children}
    </SupportChatContext.Provider>
  );
}

/** Idle state — no Centrifugo connection, no API calls */
const IDLE_SUPPORT: Omit<UseSupportReturn, never> = {
  availability: null,
  conversation: null,
  messages: [],
  sendMessage: async () => { throw new Error('Support not activated'); },
  submitContactForm: async () => { throw new Error('Support not activated'); },
  contactFormSuccess: false,
  isLoading: false,
  error: null,
};

export function SupportChatProvider({ children }: SupportChatProviderProps) {
  const [isActive, setIsActive] = useState(false);
  const [isWidgetOpen, setWidgetOpen] = useState(false);

  const activate = useCallback(() => setIsActive(true), []);

  if (isActive) {
    return (
      <ActiveSupportProvider isWidgetOpen={isWidgetOpen} setWidgetOpen={setWidgetOpen}>
        {children}
      </ActiveSupportProvider>
    );
  }

  // Idle: no useSupport() hook, no Centrifugo connection
  const value: SupportChatContextValue = {
    ...IDLE_SUPPORT,
    isWidgetOpen,
    setWidgetOpen,
    hasActiveChat: false,
    activate,
  };

  return (
    <SupportChatContext.Provider value={value}>
      {children}
    </SupportChatContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSupportChat(): SupportChatContextValue {
  const context = useContext(SupportChatContext);
  if (context === undefined) {
    throw new Error('useSupportChat must be used within a SupportChatProvider');
  }
  return context;
}
